import { isChildLineOf } from '@framebridge/toolbox/upsellEligibility';
import useCart from './cart-actions';

const baseURL = SDG.Data.apiUrl;

function useCartGiftBox() {
  const { cid } = SDG.Data;
  const { cartItems, fetchCart, updateCart } = useCart();

  const postCart = async (path, body) => {
    const response = await fetch(`${baseURL}/cart/${path}?cid=${cid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`cart ${path} failed with ${response.status}`);

    return response.json();
  };

  const findLine = (lines, lineId) => lines.find((item) => `${item.id}` === `${lineId}`);

  const currentParentLine = (parentLineId, frameSpec) => {
    const line = findLine(cartItems.value, parentLineId);

    if (!line || line.properties?._frame_spec_number !== frameSpec.number) {
      throw new Error('cart line no longer matches this frame spec');
    }

    return line;
  };

  const giftBoxCharge = (frameSpec) => (
    (frameSpec.price?.price_breakdown || []).find((charge) => charge.key === 'gift_box')
  );

  const childLineFor = (lines, parentLine, variantId) => lines.find((line) => (
    `${line.variant_id}` === `${variantId}` && isChildLineOf(line, parentLine.properties)
  ));

  const addGiftBoxLine = (variantId, frameSpecNumber, quantity) => {
    if (!variantId) throw new Error('no gift box charge to add to the cart');

    return postCart('add', {
      items: [{
        id: `${variantId}`,
        quantity,
        properties: { _parent_design: frameSpecNumber },
      }],
    });
  };

  const syncIncludesProperty = async (lines, parentLineId, frameSpec) => {
    try {
      const line = findLine(lines, parentLineId);

      if (!line) {
        fetchCart();
        return;
      }

      const cartData = await postCart('change', {
        id: `${parentLineId}`,
        properties: { ...line.properties, Includes: frameSpec.lineIncludesProperty() },
      });

      await updateCart(cartData);
    } catch (err) {
      console.error('cart gift box includes error: ', err);
      fetchCart();
    }
  };

  const enableGiftBox = async ({ frameSpec, parentLineId, joinerySku }) => {
    const parentLine = currentParentLine(parentLineId, frameSpec);
    let cartData;

    await frameSpec.updateFromServer();
    frameSpec.setGiftBox(joinerySku);
    await frameSpec.saveToServer();
    await frameSpec.calculatePrice();

    try {
      cartData = await addGiftBoxLine(giftBoxCharge(frameSpec)?.shopify_variant, frameSpec.number, parentLine.quantity);
    } catch (err) {
      frameSpec.removeGiftBox();
      await frameSpec.saveToServer();
      await frameSpec.calculatePrice();
      throw err;
    }

    await syncIncludesProperty(cartData.items || cartItems.value, parentLineId, frameSpec);
  };

  const disableGiftBox = async ({ frameSpec, parentLineId, shopifyVariantId }) => {
    const parentLine = currentParentLine(parentLineId, frameSpec);
    const variantId = shopifyVariantId || giftBoxCharge(frameSpec)?.shopify_variant;
    const childLine = variantId && childLineFor(cartItems.value, parentLine, variantId);
    let lines = cartItems.value;

    if (childLine) {
      lines = (await postCart('change', { id: `${childLine.id}`, quantity: 0 })).items || lines;
    }

    await frameSpec.updateFromServer();
    frameSpec.removeGiftBox();

    try {
      await frameSpec.saveToServer();
      await frameSpec.calculatePrice();
    } catch (err) {
      if (childLine) await addGiftBoxLine(variantId, frameSpec.number, parentLine.quantity);
      throw err;
    }

    await syncIncludesProperty(lines, parentLineId, frameSpec);
  };

  return {
    disableGiftBox,
    enableGiftBox,
  };
}

export default useCartGiftBox;
